function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

/** @jsx jsx */
import { jsx, css } from '@emotion/core';
import React from 'react';
import styled from '@emotion/styled';
import Circle from '../../icon/Circle';
import CircleDot from '../../icon/CircleDot';
export default function Container({
  children,
  ...props
}) {
  return jsx("div", _extends({
    css: css`
        padding: 37px 28px;
      `
  }, props), children);
}
const sharedTypographyStyle = `
  font-family: Rubik;
  font-style: normal;
  margin: 0;
  padding: 0;
`;
const H1 = styled.h1`
  ${sharedTypographyStyle}
  font-weight: 500;
  font-size: 30px;
  line-height: 20px;
  letter-spacing: 0.05em;
  text-transform: uppercase;

  color: #0672ab;
`;
const H2 = styled.h2`
  ${sharedTypographyStyle}
  font-weight: bold;
  font-size: 24px;
  line-height: 28px;
  color: #0672ab;
`;
const H3 = styled.h3`
  ${sharedTypographyStyle}
  font-weight: 500;
  font-size: 20px;
  line-height: 20px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #0672ab;
`;
const H4 = styled.h4`
  ${sharedTypographyStyle}
  font-weight: bold;
  font-size: 20px;
  line-height: 24px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #0672ab;
`;
export function Typography({
  variant,
  children,
  ...props
}) {
  const element = null;

  if (variant === 'h1') {
    return jsx(H1, props, children);
  }

  if (variant === 'h2') {
    return jsx(H2, props, children);
  }

  if (variant === 'h3') {
    return jsx(H3, props, children);
  }

  if (variant === 'h4') {
    return jsx(H4, props, children);
  }

  return element;
}
export function Fieldset({
  children,
  ...props
}) {
  return jsx("fieldset", _extends({
    css: css`
        border: none;
        margin: 0;
        padding: 0;
      `
  }, props), children);
}
export const Label = styled.label`
  margin-left: 12px;
  font-family: Rubik;
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 17px;
  letter-spacing: 0.01em;
  color: #0d1f2d;
`;
const inputStyle = css`
  border: 1px solid #0d1f2d;
  box-sizing: border-box;
  border-radius: 5px;
  padding: 15px 33px 15px 16px;
  font-family: Rubik;
  font-style: normal;
  font-weight: normal;
  font-size: 15px;
  line-height: 20px;
  color: #333333;
  height: 50px;
`;
export const Input = /*#__PURE__*/React.forwardRef(({
  id,
  label,
  ...props
}, ref) => {
  return jsx(React.Fragment, null, label && jsx(Label, {
    htmlFor: id
  }, label), jsx("input", _extends({
    type: "text",
    id: id,
    ref: ref,
    css: inputStyle
  }, props)));
});
export const Button = /*#__PURE__*/React.forwardRef(({
  children,
  disabledAlt = false,
  ...props
}, ref) => {
  let disabledStyle = buttonDisabledStyle;

  if (disabledAlt) {
    disabledStyle = {};
  }

  return jsx("button", _extends({
    ref: ref,
    type: "button",
    css: [buttonStyle, disabledStyle]
  }, props), children);
});
export const Reset = /*#__PURE__*/React.forwardRef(({
  children,
  ...props
}, ref) => {
  return jsx("input", _extends({
    type: "reset",
    ref: ref,
    value: children,
    css: [buttonSharedStyle, css`
          color: #0672ab;
          border: 2px solid #0672ab;
          background-color: transparent;
        `]
  }, props));
});
export const Textarea = /*#__PURE__*/React.forwardRef(({
  id,
  label,
  error,
  maxLength = null,
  showMaxLength = true,
  ...props
}, ref) => {
  var _props$value;

  return jsx(React.Fragment, null, label && jsx(Label, {
    htmlFor: id
  }, label), jsx("textarea", _extends({
    id: id,
    maxLength: maxLength,
    ref: ref,
    css: [textAreaStyle, maxLength && css`
                padding-bottom: 30px;
              `]
  }, props)), maxLength && showMaxLength && jsx("span", {
    css: css`
              position: absolute;
              bottom: ${error ? '22px' : '11px'};
              right: 20px;
              font-style: normal;
              font-weight: normal;
              font-size: 15px;
              line-height: 20px;
              color: #0d1f2d;
            `
  }, props === null || props === void 0 ? void 0 : (_props$value = props.value) === null || _props$value === void 0 ? void 0 : _props$value.length, "/", maxLength), error && jsx("span", {
    css: css`
              color: red;
              font-size: 11px;
            `
  }, error));
});
export const RadioButton = /*#__PURE__*/React.forwardRef(({
  id,
  label,
  error,
  ...props
}, ref) => {
  return jsx(React.Fragment, null, jsx(Label, {
    htmlFor: id,
    css: radioButtonStyle
  }, jsx("input", _extends({
    ref: ref,
    type: "radio",
    id: id
  }, props)), jsx(Circle, {
    className: "empty"
  }), jsx(CircleDot, {
    className: "check"
  }), label));
});
const radioButtonStyle = css`
  font-family: Rubik;
  font-style: normal;
  font-weight: normal;
  font-size: 15px;
  line-height: 18px;
  display: flex;
  align-items: center;
  letter-spacing: 0.01em;
  color: #0d1f2d;
  margin-left: 0px;
  cursor: pointer;

  input:disabled + .empty {
    cursor: not-allowed;
  }

  input:disabled + .check {
    cursor: not-allowed;
  }

  input {
    display: none;
  }

  input:checked + .empty {
    display: none;
  }

  input:checked + .empty + .check {
    display: inline;
  }

  input + .empty + .check {
    display: none;
    color: #ce1625;
    margin-right: 11px;
  }

  input + .empty {
    display: inline;
    color: #bdbdbd;
    margin-right: 11px;
  }
`;
const textAreaStyle = css`
  border: 1px solid #0d1f2d;
  box-sizing: border-box;
  border-radius: 5px;
  display: block;
  position: relative;
  width: 100%;
  height: 238px;
  font-family: Rubik;
  font-style: normal;
  font-weight: normal;
  font-size: 15px;
  line-height: 20px;
  color: #0d1f2d;
  padding: 11px 20px;
  resize: none;
`;
export const buttonSharedStyle = css`
  height: 50px;
  border-radius: 6px;
  padding: 14px 30px;
  box-sizing: border-box;
  border: none;
  font-family: Rubik;
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 22px;
  display: flex;
  align-items: center;
  text-align: center;
  text-transform: uppercase;
  justify-content: center;
  cursor: pointer;
  :disabled {
    cursor: not-allowed;
  }
`;
const buttonStyle = css([buttonSharedStyle, css`
    background-color: #ce1625;
    color: #ffffff;

    :disabled {
      cursor: not-allowed;
    }
  `]);
const buttonDisabledStyle = css`
  :disabled {
    background-color: #a0b9c8;
    color: #214a61;
  }
`;
export function ButtonCotainer({
  children,
  ...props
}) {
  return jsx("div", _extends({
    css: css`
        display: flex;
        gap: 12px;
        align-items: center;
      `
  }, props), children);
}